import type {
  ApprovalLink,
  MeetingSurfaceCard,
  MissionControlFeedStats,
  TaskActionItem,
  TaskSurfaceCard,
} from "@/services/api/agentic-v2/types";
import type { MatrixBucket } from "@/components/home/workloadMatrix";
import type { MeetingSummary } from "@/services/api/meetings/types";
import type { ApprovalJumpLink } from "@/components/home/approvalQueue";
import type {
  HomeStatItem,
  HomeMeeting,
  HomeTask,
} from "@/components/home/types";
import { rankHomeTasks } from "@/components/home/taskRanking";
import { resolveReviewClientName } from "@/components/review/saltusOnboardingReview";
import {
  reviewQueueClientName,
  reviewQueueTitle,
} from "@/components/agentic-surface/widgets/home/reviewQueueModel";

function matrixBucketFromCard(bucket: string | null | undefined): MatrixBucket | undefined {
  if (bucket === "done" || bucket === "due" || bucket === "chasing" || bucket === "stale") {
    return bucket;
  }
  return undefined;
}

/** Map server TaskSurfaceCard to legacy HomeTask shape. */
export function mapSurfaceCardToHomeTask(card: TaskSurfaceCard): HomeTask {
  return {
    id: card._id,
    title: card.presentation?.title ?? card.name,
    clientName: reviewQueueClientName(card) ?? card.entity?.display_name ?? card.name,
    missionName: card.mission?.name ?? undefined,
    missionId: card.mission?.id ?? undefined,
    missionStepKey: card.mission_step_key ?? undefined,
    matrixRowKey: card.matrix_row_key ?? undefined,
    hitlType: (card.hitl_type as HomeTask["hitlType"]) ?? "review",
    category: (card.surface_category as HomeTask["category"]) ?? "other",
    slaMinutesRemaining: card.sla_minutes_remaining ?? 240,
    risk: (card.risk_tier as HomeTask["risk"]) ?? "low",
    blockingSomeone: card.blocking_someone ?? false,
    commentCount: 0,
    ageHours: card.age_hours ?? 0,
    matrixBucket: matrixBucketFromCard(card.matrix_bucket),
    status: card.status,
    agenticTaskId: card._id,
    faClientId: card.entity?.entity_id ?? undefined,
    assignedHumanName: card.assigned_human_name ?? undefined,
    agentName: card.agent?.name ?? undefined,
    ownerRole: card.owner_role ?? undefined,
    queuedAt: card.queued_at ?? undefined,
    priority: card.priority ?? undefined,
    adviserName: card.adviser_name ?? undefined,
  };
}

/** Map needs_me task rows into a workspace task when opening the thread from review. */
export function mapTaskActionItemToHomeTask(task: TaskActionItem): HomeTask {
  const card = task as TaskSurfaceCard;
  const ageHours =
    card.age_hours ??
    (() => {
      const iso = task.queued_at ?? task.updated_at;
      if (!iso) return 0;
      const ms = Date.now() - new Date(iso).getTime();
      return Math.max(0, Math.floor(ms / (60 * 60 * 1000)));
    })();

  return {
    id: task._id,
    title: reviewQueueTitle(card),
    clientName: reviewQueueClientName(card) ?? resolveReviewClientName(task),
    missionName: task.mission?.name ?? undefined,
    missionId: task.mission?.id ?? undefined,
    missionStepKey: card.mission_step_key ?? undefined,
    matrixRowKey: card.matrix_row_key ?? undefined,
    hitlType: (card.hitl_type as HomeTask["hitlType"]) ?? "review",
    category: (card.surface_category as HomeTask["category"]) ?? "other",
    slaMinutesRemaining: card.sla_minutes_remaining ?? 240,
    risk: (card.risk_tier as HomeTask["risk"]) ?? "low",
    blockingSomeone:
      card.blocking_someone ??
      (task.status === "blocked" ||
        (task.blockers?.length ?? 0) > 0 ||
        Boolean(task.gate_blocked_reason)),
    commentCount: 0,
    ageHours,
    matrixBucket: matrixBucketFromCard(card.matrix_bucket),
    status: task.status,
    agenticTaskId: task._id,
    faClientId:
      task.entity?.entity_type === "fa_client" ? task.entity.entity_id ?? undefined : undefined,
    assignedHumanName: task.assigned_human_name ?? undefined,
    agentName: task.agent?.name ?? undefined,
    ownerRole: card.owner_role ?? undefined,
    queuedAt: task.queued_at ?? undefined,
    priority: task.priority ?? undefined,
    adviserName: task.adviser_name ?? undefined,
  };
}

export function mapApprovalLinkToJumpLink(link: ApprovalLink): ApprovalJumpLink {
  return {
    id: link.id,
    kind: link.kind as ApprovalJumpLink["kind"],
    group: link.group as ApprovalJumpLink["group"],
    label: link.label,
    title: link.title,
    body: link.body,
    ctas: link.ctas,
    clientName: link.client_name,
    entityId: link.entity_id ?? undefined,
    detail: link.detail,
    justification: link.justification ?? undefined,
    meetingId: link.meeting_id ?? undefined,
    taskId: link.task_id ?? undefined,
    missionId: link.mission_id ?? undefined,
  };
}

function formatMeetingTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatMeetingDateLabel(date: Date): string {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** Map snapshot / meetings API rows into home meeting cards. */
export function mapMeetingSummaryToHomeMeeting(
  meeting: MeetingSummary,
): HomeMeeting {
  const scheduled = meeting.scheduledAt ? new Date(meeting.scheduledAt) : null;
  const needsReview = meeting.status === "needs_review";

  return {
    id: meeting.meetingId,
    clientName: meeting.clientName ?? "Client",
    title: meeting.title,
    time: scheduled ? formatMeetingTime(scheduled) : "—",
    dateLabel: scheduled ? formatMeetingDateLabel(scheduled) : "Upcoming",
    scheduledAt: meeting.scheduledAt ?? null,
    readinessScore: needsReview ? 60 : 85,
    readinessLabel: needsReview ? "Review needed" : "On track",
    briefingReady: meeting.status === "scheduled" || meeting.status === "approved",
    assignedToMe: true,
    dgComplete: meeting.status !== "needs_upload",
    narrativeReviewId: needsReview ? meeting.currentTaskId ?? undefined : undefined,
  };
}

/** Map snapshot `meetings` cards to the home meetings widget. */
export function mapMeetingSurfaceCardToMeetingSummary(
  card: MeetingSurfaceCard,
): MeetingSummary {
  return {
    meetingId: card.meeting_id,
    companyId: "",
    title: card.title,
    scheduledAt: card.scheduled_at ?? null,
    clientName: card.client_name ?? null,
    clientId: card.client_id ?? null,
    missionId: card.mission_id ?? null,
    status: card.status as MeetingSummary["status"],
    currentTaskId: card.current_task_id ?? null,
    createdBy: "",
    createdAt: "",
    updatedAt: "",
  };
}

/** Map snapshot `meetings` cards to the home meetings widget. */
export function mapMeetingSurfaceCardToHomeMeeting(
  card: MeetingSurfaceCard,
): HomeMeeting {
  const scheduled = card.scheduled_at ? new Date(card.scheduled_at) : null;
  const needsReview = card.status === "needs_review";

  return {
    id: card.meeting_id,
    clientName: card.client_name ?? "Client",
    title: card.title,
    time: scheduled ? formatMeetingTime(scheduled) : "—",
    dateLabel: scheduled ? formatMeetingDateLabel(scheduled) : "Upcoming",
    scheduledAt: card.scheduled_at ?? null,
    readinessScore: needsReview ? 60 : 85,
    readinessLabel: needsReview ? "Review needed" : "On track",
    briefingReady:
      card.has_briefing ?? (card.status === "scheduled" || card.status === "approved"),
    assignedToMe: true,
    dgComplete: card.status !== "needs_upload",
    narrativeReviewId: needsReview ? card.current_task_id ?? undefined : undefined,
    briefingPanels: card.briefing_panels?.length ? card.briefing_panels : undefined,
  };
}

/** Standup brief from snapshot stats + optional Mission Control feed counters. */
export function buildStandupBrief(
  tasks: HomeTask[],
  stats: HomeStatItem[],
  personaName = "there",
  feedStats?: MissionControlFeedStats | null,
  inProgressTasks: HomeTask[] = [],
): string {
  const active = tasks.filter((task) => !task.completed);
  const next = rankHomeTasks(active)[0];
  const created = stats.find((row) => row.id === "created")?.value;
  const duplicates = stats.find((row) => row.id === "duplicates")?.value ?? 0;
  const lost = stats.find((row) => row.id === "lost")?.value ?? 0;
  const due = stats.find((row) => row.id === "due")?.value ?? 0;
  const chasing = stats.find((row) => row.id === "chasing")?.value ?? 0;
  const stale = stats.find((row) => row.id === "stale")?.value ?? 0;
  const booked = stats.find((row) => row.id === "booked")?.value ?? 0;

  const pipelineLine =
    created !== undefined
      ? `${created} leads created, ${duplicates} duplicates, ${lost} lost in your onboarding caseload.`
      : `${due} due, ${chasing} chasing, ${stale} stale, ${booked} booked across your caseload.`;

  const lines = [
    `Hi ${personaName} — here's your pipeline at a glance.`,
    pipelineLine,
  ];

  const needsAttention = feedStats?.needs_attention_now ?? 0;
  if (needsAttention > 0) {
    lines.push(
      `${needsAttention} item${needsAttention === 1 ? "" : "s"} need attention right now.`,
    );
  } else if (inProgressTasks.length > 0) {
    const top = inProgressTasks[0];
    const caseLabel =
      inProgressTasks.length === 1 ? "1 onboarding case" : `${inProgressTasks.length} onboarding cases`;
    lines.push(
      `${caseLabel} in progress — Ani is working on ${top.title} for ${top.clientName}.`,
    );
  } else if (next) {
    lines.push(`Next up: ${next.title} for ${next.clientName}.`);
  } else if (active.length === 0) {
    lines.push("You're clear on assigned tasks right now.");
  }

  return lines.join("\n");
}
